import { useId, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { UserPlus } from 'lucide-react'
import type {
  CsvImportApplyResponse,
  CsvImportPreviewResponse,
  Entitlements,
  HouseholdRole,
  Invitation,
  Member,
  NotificationPreferencesResponse,
  NotificationPreset,
} from '@ousse/shared'
import { NOTIFICATION_PRESETS } from '@ousse/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useHousehold } from '../lib/household'
import { ConfirmButton, CopyButton, ErrorAlert, Field } from '../components/ui'

async function downloadExport(householdId: string, format: 'json' | 'csv', onError: (e: unknown) => void) {
  try {
    onError(null)
    const res = await fetch(`/api/v1/households/${householdId}/export.${format}`, { credentials: 'include' })
    if (!res.ok) throw new Error(`export_${res.status}`)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ousse-export.${format}`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    onError(e)
  }
}

/** Mon foyer : nom, personnes, invitations (lien ou code). */
export function HouseholdPage() {
  const { t } = useTranslation()
  const { me } = useAuth()
  const { current } = useHousehold()
  const qc = useQueryClient()
  const [error, setError] = useState<unknown>(null)
  const hid = current?.id
  const isOwner = current?.role === 'owner'

  const members = useQuery({
    queryKey: ['members', hid],
    enabled: Boolean(hid),
    queryFn: () => api.get<{ items: Member[] }>(`/households/${hid}/members`),
  })
  const invitations = useQuery({
    queryKey: ['invitations', hid],
    enabled: Boolean(hid) && isOwner,
    queryFn: () => api.get<{ items: Invitation[] }>(`/households/${hid}/invitations`),
  })
  const entitlements = useQuery({
    queryKey: ['entitlements', hid],
    enabled: Boolean(hid),
    queryFn: () => api.get<Entitlements>(`/households/${hid}/entitlements`),
  })
  const run = (fn: () => Promise<unknown>) => async () => {
    setError(null)
    try {
      await fn()
      await qc.invalidateQueries()
    } catch (e) {
      setError(e)
    }
  }

  if (!current) return <p role="status">{t('common.loading')}</p>
  const me_id = me!.user.id
  const owners = members.data?.items.filter((m) => m.role === 'owner').length ?? 0
  // Le dernier propriétaire ne peut pas partir (l'API refuse avec `last_owner`) : on n'affiche pas le bouton.
  const canLeave = current.role !== 'owner' || owners > 1
  const canWrite = current.role !== 'viewer' && !current.readOnly

  return (
    <>
      <h1>{current.name}</h1>
      {entitlements.data && (
        <p className="muted">
          {t('household.plan')} : {entitlements.data.plan.name}
        </p>
      )}
      <ErrorAlert error={error} />
      {isOwner && <RenameForm householdId={current.id} name={current.name} onError={setError} />}

      <h2>{t('export.title')}</h2>
      <p className="muted">{t('export.hint')}</p>
      <div className="row">
        <button
          type="button"
          className="btn secondary"
          onClick={() => void downloadExport(current.id, 'json', setError)}
        >
          {t('export.downloadJson')}
        </button>
        <button
          type="button"
          className="btn secondary"
          onClick={() => void downloadExport(current.id, 'csv', setError)}
        >
          {t('export.downloadCsv')}
        </button>
      </div>

      {!me?.user.simplifiedMode && canWrite && (
        <CsvImportSection
          householdId={current.id}
          featureEnabled={entitlements.data?.features.csvImport !== false}
          onError={setError}
        />
      )}

      <NotificationPrefsSection householdId={current.id} onError={setError} />

      <h2>{t('household.members')}</h2>
      <ul className="list">
        {members.data?.items.map((m) => (
          <li key={m.userId}>
            <span>
              <strong>{m.displayName}</strong> {m.userId === me_id && <span className="muted">{t('household.you')}</span>}
              <br />
              <span className="muted">{t(`household.roleShort.${m.role}`)}</span>
            </span>
            <span className="row">
              {isOwner && m.userId !== me_id && (
                <div className="field">
                  <label htmlFor={`role-${m.userId}`}>{t('household.inviteRole')}</label>
                  <select
                    id={`role-${m.userId}`}
                    value={m.role}
                    onChange={(e) => void run(() => api.patch(`/households/${current.id}/members/${m.userId}`, { role: e.target.value }))()}
                  >
                    {(['owner', 'member', 'viewer'] as const).map((r) => (
                      <option key={r} value={r}>
                        {t(`household.roleShort.${r}`)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {m.userId === me_id ? (
                canLeave && (
                <ConfirmButton message={t('household.leaveConfirm')} onConfirm={run(() => api.delete(`/households/${current.id}/members/${m.userId}`))}>
                  {t('household.leave')}
                </ConfirmButton>
                )
              ) : (
                isOwner && (
                  <ConfirmButton
                    message={t('household.removeConfirm', { name: m.displayName })}
                    onConfirm={run(() => api.delete(`/households/${current.id}/members/${m.userId}`))}
                  >
                    {t('household.removeMember')}
                  </ConfirmButton>
                )
              )}
            </span>
          </li>
        ))}
      </ul>

      {isOwner && (
        <>
          <h2>{t('household.invite')}</h2>
          <InviteForm householdId={current.id} onError={setError} />
          <h2>{t('household.invitations')}</h2>
          {invitations.data?.items.length ? (
            <ul className="list">
              {invitations.data.items.map((i) => (
                <li key={i.id}>
                  <span>
                    <span className="code">{i.code}</span>
                    <br />
                    <span className="muted">{t(`household.roleShort.${i.role}`)}</span>
                  </span>
                  <ConfirmButton message={t('household.revokeConfirm')} onConfirm={run(() => api.delete(`/households/${current.id}/invitations/${i.id}`))}>
                    {t('household.revoke')}
                  </ConfirmButton>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t('household.noInvitations')}</p>
          )}
        </>
      )}
    </>
  )
}

function NotificationPrefsSection({
  householdId,
  onError,
}: {
  householdId: string
  onError: (e: unknown) => void
}) {
  const { t } = useTranslation()
  const prefs = useQuery({
    queryKey: ['notification-preferences', householdId],
    queryFn: () =>
      api.get<NotificationPreferencesResponse>(`/households/${householdId}/notification-preferences`),
  })
  const qc = useQueryClient()
  const save = useMutation({
    mutationFn: (preset: NotificationPreset) =>
      api.put<NotificationPreferencesResponse>(`/households/${householdId}/notification-preferences`, {
        preset,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['notification-preferences', householdId] })
    },
    onError,
  })
  const current = prefs.data?.preset ?? 'everything'

  return (
    <>
      <h2>{t('notifications.preferences')}</h2>
      <p className="muted">{t('notifications.preferencesHint')}</p>
      <p className="muted">{t('notifications.pushHint')}</p>
      <ErrorAlert error={prefs.error} />
      <fieldset className="radio-group">
        <legend>{t('notifications.preferences')}</legend>
        {NOTIFICATION_PRESETS.map((preset) => (
          <label key={preset} className="checkbox">
            <input
              type="radio"
              name="notif-preset"
              value={preset}
              checked={current === preset}
              disabled={save.isPending || prefs.isLoading}
              onChange={() => {
                onError(null)
                save.mutate(preset)
              }}
            />
            <span>{t(`notifications.preset.${preset}`)}</span>
          </label>
        ))}
      </fieldset>
    </>
  )
}

function CsvImportSection({
  householdId,
  featureEnabled,
  onError,
}: {
  householdId: string
  featureEnabled: boolean
  onError: (e: unknown) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const fileId = useId()
  const [csv, setCsv] = useState('')
  const [preview, setPreview] = useState<CsvImportPreviewResponse | null>(null)
  const [result, setResult] = useState<CsvImportApplyResponse | null>(null)
  const [busy, setBusy] = useState(false)

  const downloadTemplate = async () => {
    try {
      onError(null)
      const res = await fetch(`/api/v1/households/${householdId}/import/template.csv`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`import_template_${res.status}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'ousse-import-modele.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      onError(e)
    }
  }

  const runPreview = async () => {
    if (!csv.trim()) return
    setBusy(true)
    setResult(null)
    onError(null)
    try {
      setPreview(await api.post<CsvImportPreviewResponse>(`/households/${householdId}/import/preview`, { csv }))
    } catch (e) {
      setPreview(null)
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  const runApply = async () => {
    if (!preview || preview.okCount === 0) return
    setBusy(true)
    onError(null)
    try {
      const res = await api.post<CsvImportApplyResponse>(`/households/${householdId}/import/apply`, { csv })
      setResult(res)
      setPreview(null)
      setCsv('')
      await qc.invalidateQueries()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  if (!featureEnabled) {
    return (
      <>
        <h2>{t('import.title')}</h2>
        <p className="muted">{t('import.featureLocked')}</p>
      </>
    )
  }

  return (
    <>
      <h2>{t('import.title')}</h2>
      <p className="muted">{t('import.hint')}</p>
      <div className="row">
        <button type="button" className="btn secondary" onClick={() => void downloadTemplate()}>
          {t('import.downloadTemplate')}
        </button>
      </div>
      <div className="field">
        <label htmlFor={fileId}>{t('import.chooseFile')}</label>
        <input
          id={fileId}
          type="file"
          accept=".csv,text/csv,text/plain"
          disabled={busy}
          onChange={async (e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (!file) return
            onError(null)
            setResult(null)
            setPreview(null)
            setCsv(await file.text())
          }}
        />
      </div>
      <div className="field">
        <label htmlFor={`${fileId}-text`}>{t('import.title')}</label>
        <textarea
          id={`${fileId}-text`}
          rows={6}
          value={csv}
          disabled={busy}
          onChange={(e) => {
            setCsv(e.target.value)
            setPreview(null)
            setResult(null)
          }}
          style={{ width: '100%', fontFamily: 'inherit' }}
        />
      </div>
      <div className="row">
        <button type="button" className="btn secondary" disabled={busy || !csv.trim()} onClick={() => void runPreview()}>
          {t('import.preview')}
        </button>
        {preview && preview.okCount > 0 && !busy && (
          <ConfirmButton
            message={t('import.applyConfirm', {
              count: preview.okCount,
              locations: preview.locationsToCreateCount,
            })}
            onConfirm={() => void runApply()}
          >
            {t('import.apply', { count: preview.okCount })}
          </ConfirmButton>
        )}
      </div>
      {result && (
        <p role="status">
          {t('import.result', {
            items: result.createdItems,
            locations: result.createdLocations,
            skipped: result.skipped,
          })}
        </p>
      )}
      {preview && (
        <div className="stack">
          <p className="muted">
            {preview.rows.length === 0
              ? t('import.noRows')
              : t('import.summary', {
                  ok: preview.okCount,
                  errors: preview.errorCount,
                  locations: preview.locationsToCreateCount,
                })}
          </p>
          {preview.rows.length > 0 && (
            <ul className="list">
              {preview.rows.map((row) => (
                <li key={row.line}>
                  <div className="stack" style={{ flex: 1 }}>
                    <strong>
                      {t('import.line', { line: row.line })}
                      {row.name ? ` — ${row.name}` : ''}
                    </strong>
                    {row.locationPath && <span className="muted">{row.locationPath}</span>}
                    {row.locationsToCreate.length > 0 && (
                      <span className="muted">
                        {t('import.willCreate', { path: row.locationsToCreate.join(' › ') })}
                      </span>
                    )}
                    {row.errors.length > 0 && (
                      <span role="alert">
                        {row.errors
                          .map((code) => {
                            const key = `import.errors.${code}`
                            const translated = t(key)
                            return translated === key ? code : translated
                          })
                          .join(' · ')}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  )
}

function RenameForm({ householdId, name, onError }: { householdId: string; name: string; onError: (e: unknown) => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [value, setValue] = useState(name)
  const mutation = useMutation({
    mutationFn: () => api.patch(`/households/${householdId}`, { name: value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['households'] }),
    onError,
  })
  return (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault()
        mutation.mutate()
      }}
    >
      <Field label={t('household.name')} required maxLength={80} value={value} onChange={(e) => setValue(e.target.value)} />
      <button className="btn secondary" disabled={mutation.isPending} style={{ alignSelf: 'end' }}>
        {t('household.rename')}
      </button>
    </form>
  )
}

const EXPIRES_IN_DAYS = 7

function InviteForm({ householdId, onError }: { householdId: string; onError: (e: unknown) => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [role, setRole] = useState<Exclude<HouseholdRole, 'owner'>>('member')
  const [created, setCreated] = useState<Invitation | null>(null)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    try {
      setCreated(await api.post<Invitation>(`/households/${householdId}/invitations`, { role, expiresInDays: EXPIRES_IN_DAYS }))
      await qc.invalidateQueries({ queryKey: ['invitations', householdId] })
    } catch (err) {
      onError(err)
    }
  }
  if (created) {
    return (
      <div className="card stack">
        <p>{t('household.inviteIntro', { count: EXPIRES_IN_DAYS })}</p>
        <p className="code">{created.code}</p>
        <p style={{ wordBreak: 'break-all' }}>{created.url}</p>
        <div className="row">
          <CopyButton value={created.url} />
          <button type="button" className="btn ghost" onClick={() => setCreated(null)}>
            {t('common.close')}
          </button>
        </div>
      </div>
    )
  }
  return (
    <form className="card stack" onSubmit={submit}>
      <fieldset className="radio-group">
        <legend>{t('household.inviteRole')}</legend>
        {(['member', 'viewer'] as const).map((r) => (
          <label key={r} className="checkbox">
            <input type="radio" name="role" value={r} checked={role === r} onChange={() => setRole(r)} />
            <span>{t(`household.roles.${r}`)}</span>
          </label>
        ))}
      </fieldset>
      <button className="btn">
        <UserPlus aria-hidden size={20} />
        {t('household.createInvite')}
      </button>
    </form>
  )
}
